import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';

// components
import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { ServerVarsContextConsumer } from '../global/ServerVarsContext/ServerVarsContext';
import { PdpHead } from './PdpHead';
import { SharedTrackingContainer } from '../shared/SharedTrackingContainer';
import serverVars from 'server-vars';
import { CUSTOMER_TYPE_TRADE } from '../constants/userConstants';

// helpers
import { getPdpTileTestVariables } from './helpers/pdpTileTest';
import get from 'lodash.get';
import { trackEcommerce } from './tracking/pdpTracking';

export class PdpSharedLayoutComponent extends React.Component {
    constructor(props) {
        super(props);

        this.getFollowSearchData = this.getFollowSearchData.bind(this);
        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.trackingFunction = this.trackingFunction.bind(this);
    }

    getFollowSearchData() {
        const searchPage = get(this.props, 'viewer.item.saveSearchData.page');
        return { followSearchPages: [searchPage], fetchFollowSearch: !!searchPage };
    }

    handleUserRefetch(variables, renderVariables, callback) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         *
         * Only do this after login, hence the server vars check, subsequent visits will depend on
         * the ut query param.
         */
        const { canRunPdpTileTest } = this.props;
        const fetchPdp = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;
        const isMultiSku = get(this.props, 'viewer.item.isMultiSku') || false;

        const abTestVars = getPdpTileTestVariables(canRunPdpTileTest, true);
        const refetchVariables = {
            ...variables,
            ...abTestVars,
            ...this.getFollowSearchData(),
            fetchPdp,
            isMultiSku,
        };
        const updatedRenderVariables = {
            ...refetchVariables,
            ...renderVariables,
            fetchPdp: true,
        };
        this.props.doRefetch(refetchVariables, updatedRenderVariables, err => {
            callback(err, updatedRenderVariables);
        });
    }

    getTrackingData() {
        const params = get(this.props, 'viewer.item.pdpTrackingVpvParams') || {};
        const { additionalTrackingData } = this.props;
        return {
            ...params,
            ...additionalTrackingData,
            hasViewInRoom: get(this.props, 'viewer.item.viewInRoomPhotos', []).length,
        };
    }

    trackingFunction() {
        const item = get(this.props, 'viewer.item');
        trackEcommerce(item);
    }

    render() {
        const { user } = this.props;
        const { url: opt_pageURL, ...additional } = this.getTrackingData() || {}; // eslint-disable-line camelcase
        const gtmPageInfo = { opt_pageURL }; // eslint-disable-line camelcase
        const { fetchFollowSearch } = this.getFollowSearchData();

        return (
            <ServerVarsContextConsumer>
                {({ clientConf }) => {
                    const pdpHead = new PdpHead({ ...this.props, clientConf });

                    return (
                        <React.Fragment>
                            <SharedUserAndRelayVarLoader onUserRefetch={this.handleUserRefetch} />
                            <CurrencyProvider
                                render={({ currency, showCurrency }) => {
                                    return (
                                        <React.Fragment>
                                            <SharedTrackingContainer
                                                currency={currency}
                                                user={user}
                                                gtmPageInfo={gtmPageInfo}
                                                onTrackingFired={this.trackingFunction}
                                                additional={additional}
                                            />
                                            {this.props.children({
                                                pdpHead,
                                                currency,
                                                showCurrency,
                                                fetchFollowSearch,
                                            })}
                                        </React.Fragment>
                                    );
                                }}
                            />
                        </React.Fragment>
                    );
                }}
            </ServerVarsContextConsumer>
        );
    }
}

PdpSharedLayoutComponent.defaultProps = {
    additionalTrackingData: {},
    canRunPdpTileTest: false,
};

PdpSharedLayoutComponent.propTypes = {
    children: PropTypes.func.isRequired,
    additionalTrackingData: PropTypes.object,
    doRefetch: PropTypes.func.isRequired,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    intl: intlShape.isRequired,
    canRunPdpTileTest: PropTypes.bool.isRequired,
};

function mapStateToProps({ intlVariables }) {
    return { intlVariables };
}

export const PdpSharedLayout = injectIntl(
    createFragmentContainer(connect(mapStateToProps)(PdpSharedLayoutComponent), {
        viewer: graphql`
            fragment PdpSharedLayout_viewer on Viewer {
                ...PdpHead_viewer @relay(mask: false)
                item(itemId: $itemId) {
                    isMultiSku
                    pdpTrackingVpvParams(userId: $userId) @include(if: $isClient)
                    saveSearchData {
                        page
                    }
                    ... @include(if: $fetchPdp) {
                        ...PdpHead_item @relay(mask: false)
                        pdpMeta {
                            title
                        }
                        attributes {
                            period {
                                option {
                                    name
                                }
                            }
                        }
                        browseUrl
                        categoryCode
                        seller {
                            serviceId
                            isInternal
                        }
                        viewInRoomPhotos {
                            src
                        }
                        contemporaryTrackingString
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpSharedLayout_user on User {
                ...SharedTrackingContainer_user
            }
        `,
    })
);
